import type { Filter } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Comparator } from "@amzn/d16-g-client-deck-builder-typescript-client";
import * as R from "remeda";

import type { MultiSelectDimensionItem } from "../components/dimensions/MultiSelectDimension";
import type { SegmentGroupStructure } from "../components/dimensions/vitest/TargetSegmentPickerDimension";
import type { Write } from "../store/zustand.helpers";
import {
    clearState,
    zustandStore,
    getStateKeys,
} from "../store/zustand.helpers";
import type { RequestParameters } from "../types/common";
import type { Dimension } from "../types/dimensions";
import { DimensionTypes } from "../types/dimensions";

export interface BaseDimensionValue {
    type?: DimensionTypes;
    value: string;
}

export interface DropdownDimensionValue extends BaseDimensionValue {
    label: string;
}

export interface MultiSelectDimensionValue extends BaseDimensionValue {
    type: DimensionTypes.MULTISELECT | DimensionTypes.MULTI_SELECT_STATIC;
    selectedItems: string[];
    selectedItemsObjects: MultiSelectDimensionItem[];
}

export type InputDimensionValue = BaseDimensionValue;

export type DateDimensionValue = BaseDimensionValue;

export interface DataDimensionValue extends BaseDimensionValue {}

export interface ListBuilderDimensionValue extends BaseDimensionValue {
    type: DimensionTypes.LIST_BUILDER;
    selectedItems: string[];
}

export interface TargetSegmentPickerDimensionValue extends BaseDimensionValue {
    type: DimensionTypes.TARGET_SEGMENT_PICKER;
    inclusionSegmentsGroup: {
        matchCriteria: string;
        segmentList: Record<string, SegmentGroupStructure>;
    };
    exclusionSegmentsGroup: {
        matchCriteria: string;
        segmentList: Record<string, SegmentGroupStructure>;
    };
}

export type DimensionValue =
    | InputDimensionValue
    | DropdownDimensionValue
    | DataDimensionValue
    | MultiSelectDimensionValue
    | TargetSegmentPickerDimensionValue
    | ListBuilderDimensionValue;

export interface DimensionData {
    data: DimensionValue;
    requestParameters: RequestParameters;
}

interface DimensionValueState {
    [key: string]: DimensionData | undefined;
}

interface DimensionValueActions {
    setData: (
        dimension: string,
        data: DimensionValue,
        requestParameters: RequestParameters,
    ) => void;

    /**
     * clears all values within the store
     */
    clearAllValues: () => void;

    /**
     * helper method that guarantees return value of dimension data.
     */
    getDimensionData: (dimension: string) => DimensionValue;

    /**
     * initialize provided dimensions with empty values
     */
    initDimensions: (dimensions: Dimension[]) => void;

    /**
     * Reset current dimension values
     */
    resetCurrentDimensions: () => void;

    /**
     * helper method that returns a list of filters of dimension id to dimension value.
     */
    getDimensionValueFilters: (dimensions: Dimension[]) => Filter[];

    /**
     * get object of ids mapped to value of given dimension ids
     * @param dimensions list of dimension ids
     * @returns object of ids mapped to value
     */
    getDimensionsValuesMapped: (dimensions: string[]) => {
        [key in string]: string;
    };
}

const initialState: DimensionValueState = {};

export const newDimensionData: (
    value?: string,
    type?: DimensionTypes,
) => DimensionData = (value = "", type?: DimensionTypes) => {
    if (type && DimensionTypeDefaultValue[type]) {
        const data = DimensionTypeDefaultValue[type];
        return {
            data: {
                ...data,
                value,
            },
            requestParameters: {},
        };
    }
    return {
        data: { value },
        requestParameters: {},
    };
};

export const DimensionTypeDefaultValue: {
    [key in DimensionTypes]?: DimensionValue;
} = {
    [DimensionTypes.LIST_BUILDER]: {
        type: DimensionTypes.LIST_BUILDER,
        value: "",
        selectedItems: [],
    },
};

export const { store: dimensionValueStore } = zustandStore<
    DimensionValueState,
    DimensionValueActions
>("Dimension Value", initialState, (set, get) => ({
    setData: (dimension, data, requestParameters) =>
        set(() => ({ [dimension]: { data, requestParameters } })),
    clearAllValues: () => set((state) => clearState(state), true),
    getDimensionData: (dimension) => {
        const selectedDimension = get()[dimension];
        if (selectedDimension) {
            return selectedDimension.data;
        } else {
            return newDimensionData().data;
        }
    },
    getDimensionValueFilters: (dimensions) => {
        const store = get() as Write<
            DimensionValueState,
            DimensionValueActions
        >;
        const dimensionsValueFilters: Filter[] = [];

        dimensions.forEach((dimension) => {
            dimensionsValueFilters.push({
                key: dimension,
                comparator: Comparator.EQUALS,
                value: store.getDimensionData(dimension).value,
            });
        });

        return dimensionsValueFilters;
    },
    initDimensions: (dimensions) => {
        set(() => R.flatMapToObj(dimensions, (e) => [[e, newDimensionData()]]));
    },
    resetCurrentDimensions: () => {
        const store = get() as Write<
            DimensionValueState,
            DimensionValueActions
        >;
        const dimensions = getStateKeys(store);
        store.initDimensions(dimensions);
    },

    getDimensionsValuesMapped: (dimensions) => {
        const store = get() as Write<
            DimensionValueState,
            DimensionValueActions
        >;
        if (dimensions.length > 0) {
            return R.pipe(
                dimensions,
                R.map((e) => [e, store.getDimensionData(e).value]),
                R.fromEntries,
            );
        }
        return {};
    },
}));
